// utils
import { getLoginPageTypeAndSchema } from '@shopee-user/business-customization-logic';
import { convertBiometricTypeToTrackingData } from '@tob/shared/biometrics/tracking/utils';

// constants
import { LoginTrackingPoint } from '@shopee-user/tracking-constants';
import { TrackingOperation } from '@shopee-user/tracking';

// types
import {
  TrackingDataLoginProps,
  TrackingDataFieldLogin,
  TrackingDataLoginErrorProps,
  TrackingDataFieldLoginError,
  TrackingDataFieldTypeAccountType,
  TrackingDataFieldLoginByToken,
  TrackingDataFieldLoginErrorAction,
  TrackingDataLoginErrorActionProps,
} from './types';
import { TrackingInfo, ImpressionTrackingInfo } from '@shopee-user/tracking';
import { converOtpTypeToPhoneVerificationChannel } from '@tob/shared/whatsapp-auth-link/tracking/utils';
import { OtpType } from '@shopee-user/otp-hook-tob';
export function generateTrackingLoginView({
  loginMethod,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.View,
    data: { login_method: loginMethod },
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.PageView),
  };
}

export function generateTrackingLoginWithPasswordLoginButtonClick({
  loginMethod,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_button',
    data: { login_method: loginMethod },
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.LoginButtonClick),
  };
}

export function generateTrackingLoginForgotPasswordButtonClick({
  loginMethod,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'forgot_button',
    data: { login_method: loginMethod },
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.ForgotButtonClick),
  };
}

export function generateTrackingLoginBackButtonClick({
  loginMethod,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'back_button',
    data: { login_method: loginMethod },
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.BackButtonClick),
  };
}

export function generateTrackingLoginFailAction({
  loginMethod,
  accountType,
  errorCode,
}: TrackingDataLoginErrorActionProps): TrackingInfo<
  TrackingDataFieldLoginErrorAction
> {
  return {
    operation: TrackingOperation.LoginFailAction,
    data: {
      login_method: loginMethod,
      error_code: errorCode,
      account_type: accountType,
    },
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.LoginFailAction),
  };
}

export function generateTrackingLoginSuccessAction({
  loginMethod,
  accountType,
  biometricType,
  otpType,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.LoginSuccessAction,
    data: {
      login_method: loginMethod,
      account_type: accountType,
      biometric_type: biometricType
        ? convertBiometricTypeToTrackingData(biometricType)
        : undefined,
      phone_verification_channel: converOtpTypeToPhoneVerificationChannel(
        otpType as OtpType
      ),
    },
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.LoginSuccessAction),
  };
}

export function generateTrackingLoginHelpButtonClick({
  loginMethod,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'help_button',
    data: { login_method: loginMethod },
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.HelpButtonClick),
  };
}

export function generateTrackingLoginSignupButtonClick({
  loginMethod,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'sign_up_button',
    data: { login_method: loginMethod },
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.SignupButtonClick),
  };
}

export function generateTrackingLoginWithSMSButtonClick({
  loginMethod,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_sms_button',
    data: { login_method: loginMethod },
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.LoginWithSMSButtonClick),
  };
}

export function generateTrackingLoginWithSMSNextButtonClick({
  loginMethod,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'next_button',
    data: { login_method: loginMethod },
    ...getLoginPageTypeAndSchema(
      LoginTrackingPoint.LoginWithSMSNextButtonClick
    ),
  };
}

export function generateTrackingLoginWithPasswordButtonClick({
  loginMethod,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_password_button',
    data: { login_method: loginMethod },
    ...getLoginPageTypeAndSchema(
      LoginTrackingPoint.LoginWithSMSLoginWithPasswordButtonClick
    ),
  };
}

export function generateTrackingLoginWithBusinessGoogleButtonClick(): TrackingInfo<
  TrackingDataFieldLogin
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'google_button',
    ...getLoginPageTypeAndSchema(
      LoginTrackingPoint.LoginWithBusinessGoogleButtonClick
    ),
  };
}

// the captcha poup generate functions will be returning a function instead of an object as
// the we do not want to expose the login_method data to the captcha component
type CaptchaPopupImpressionTrackingInfoGenerator = () => ImpressionTrackingInfo<
  TrackingDataFieldLogin
>;

export function generateTrackingLoginCaptchaPopupImpression({
  loginMethod,
}: TrackingDataLoginProps): CaptchaPopupImpressionTrackingInfoGenerator {
  return () => {
    return {
      operation: TrackingOperation.Impression,
      target_type: 'captcha_popup',
      page_section: 'captcha_popup',
      data: { login_method: loginMethod },
      ...getLoginPageTypeAndSchema(
        LoginTrackingPoint.LoginWithSMSCaptchaPopupImpression
      ),
    };
  };
}

type CaptchaPopupTrackingInfoGenerator = () => TrackingInfo<
  TrackingDataFieldLogin
>;

export function generateTrackingLoginCaptchaPopupCancelButtonClick({
  loginMethod,
}: TrackingDataLoginProps): CaptchaPopupTrackingInfoGenerator {
  return () => {
    return {
      operation: TrackingOperation.Click,
      target_type: 'cancel_button',
      page_section: 'captcha_popup',
      data: { login_method: loginMethod },
      ...getLoginPageTypeAndSchema(
        LoginTrackingPoint.LoginWithSMSCaptchaPopupCancelButtonClick
      ),
    };
  };
}

export function generateTrackingLoginCaptchaPopupOkButtonClick({
  loginMethod,
}: TrackingDataLoginProps): CaptchaPopupTrackingInfoGenerator {
  return () => {
    return {
      operation: TrackingOperation.Click,
      target_type: 'ok_button',
      page_section: 'captcha_popup',
      data: { login_method: loginMethod },
      ...getLoginPageTypeAndSchema(
        LoginTrackingPoint.LoginWithSMSCaptchaPopupOkButtonClick
      ),
    };
  };
}

export function generateTrackingLoginCaptchaPopupRefreshButtonClick({
  loginMethod,
}: TrackingDataLoginProps): CaptchaPopupTrackingInfoGenerator {
  return () => {
    return {
      operation: TrackingOperation.Click,
      target_type: 'refresh_button',
      page_section: 'captcha_popup',
      data: { login_method: loginMethod },
      ...getLoginPageTypeAndSchema(
        LoginTrackingPoint.LoginWithSMSCaptchaPopupRefreshButtonClick
      ),
    };
  };
}

export function generateTrackingLoginFailedToastAction({
  loginMethod,
  error,
  errorMsg,
}: TrackingDataLoginErrorProps): TrackingInfo<TrackingDataFieldLoginError> {
  return {
    operation: TrackingOperation.ErrorToastAction,
    data: {
      login_method: loginMethod,
      error_code: error,
      error_message: errorMsg,
    },
    target_type: 'login_failed_toast',
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.LoginFailedToastAction),
  };
}

export function generateTrackingLoginAddPhoneEmailPopupView(): TrackingInfo<
  undefined
> {
  return {
    operation: TrackingOperation.Impression,
    page_section: 'add_phone_or_email_popup',
    ...getLoginPageTypeAndSchema(
      LoginTrackingPoint.LoginWithPasswordAddPhoneEmailPopupView
    ),
  };
}

export function generateTrackingLoginAddPhoneEmailPopupOkClick(): TrackingInfo<
  undefined
> {
  return {
    operation: TrackingOperation.Click,
    page_section: 'add_phone_or_email_popup',
    target_type: 'ok_button',
    ...getLoginPageTypeAndSchema(
      LoginTrackingPoint.LoginWithPasswordAddPhoneEmailPopupOkClick
    ),
  };
}

export function generateTrackingLoginByTokenSuccessAction({
  accountType,
}: {
  accountType: TrackingDataFieldTypeAccountType;
}): TrackingInfo<TrackingDataFieldLoginByToken> {
  return {
    operation: TrackingOperation.LoginTokenSuccessAction,
    data: {
      account_type: accountType,
    },
    ...getLoginPageTypeAndSchema(LoginTrackingPoint.LoginByTokenSuccessAction),
  };
}
